import { useState, useEffect, useRef } from 'react';
import firebase from "firebase/app";
import "firebase/auth";
import "firebase/firebase-firestore";
import "firebase/storage";
import { Collections } from '../../../types/collection';
import { INTERVIEW_TYPES } from '../../../pages/NewSession';

interface CountdownProps {
    startSeconds: number;
    sessionId: string;
    runId: string;
    onComplete: () => void;
    dontCountSignal: boolean;
    interviewType: INTERVIEW_TYPES;
}

function Countdown({ startSeconds, sessionId, runId, onComplete, dontCountSignal, interviewType }: CountdownProps) {
    const [secondsRemaining, setSecondsRemaining] = useState(startSeconds);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        // Define the doc reference - adjust your path as needed
        // const docRef = doc(db, 'countdowns', docId);

        // Function to decrement and update Firestore
        if(dontCountSignal) return;
        const tick = async () => {
            setSecondsRemaining(prev => {
                const newValue = prev - 1;
                return newValue >= 0 ? newValue : 0;
            });
        };

        // Start the interval
        intervalRef.current = setInterval(tick, 1000);

        return () => {
            // Cleanup the interval on unmount
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [startSeconds, sessionId, dontCountSignal]);

    useEffect(() => {
        if(dontCountSignal) return;
        // Whenever secondsRemaining updates, push it to Firestore
        // except before the first render which sets the initial value.
        if (secondsRemaining < startSeconds) {
            const countdownInterViewTypes = [INTERVIEW_TYPES.PHONE_INTERVIEW, INTERVIEW_TYPES.VIDEO_INTERVIEW];
            if(countdownInterViewTypes.includes(interviewType)){
                firebase.firestore()
                .collection(Collections.Runs)
                .doc(sessionId)
                .collection('list')
                .doc(runId)
                .update({
                    timeRemaining: secondsRemaining
                })
            } else {
                firebase.firestore()
                .collection(Collections.Sessions)
                .doc(firebase.auth().currentUser?.uid)
                .collection('list')
                .doc(sessionId)
                .update({
                    timeRemaining: secondsRemaining
                })
            }
        }

        // If time reached zero, clear the interval
        if (secondsRemaining === 0 && intervalRef.current) {
            clearInterval(intervalRef.current);
            onComplete()
        }
    }, [secondsRemaining, startSeconds, sessionId, onComplete, runId, dontCountSignal, interviewType]);

    const minutes = Math.floor(secondsRemaining / 60);
    const seconds = secondsRemaining % 60;
    const formattedTime = `${minutes}:${seconds.toString().padStart(2, '0')}`;

    return (
        <div>
            <span>Time Remaining: {formattedTime}</span>
        </div>
    );
}

export default Countdown;
